// A device request's reason and an app user's capture requests (APS-437, D114; spec 15.5): what
// the request forms send, and the state words of a capture request on the person page. Kept out
// of the page so they are tested on their own.
import type { NewDeviceRequest } from '../api/queries'
import type { DeviceRequest } from '../api/types'
import { FAMILIES } from './signals'

/**
 * The shortest user hash a request takes (APS-541): the API keeps a request's last four
 * characters as its hint, so a shorter hash is asked for by its install instead.
 */
export const REQUEST_MIN_USER_HASH = 16

/** A request's reason as the API takes it: 1 to 200 characters, trimmed. */
export const REASON_MAX = 200
/** A capture's next sessions, 1 to 20; the form starts at 1 (APS-541). */
export const CAPTURE_SESSIONS = { min: 1, max: 20, default: 1 } as const

/** What a capture sends (APS-541): every family, or every family but replay. */
export type CaptureDetail = 'replay' | 'noReplay'
/** The days a request stays open if no device takes it, as Device data offers them. */
export const REQUEST_DAYS = ['1', '3', '7'] as const

/** The rule every reason follows: no email (the API refuses one); a ticket number is enough. */
const EMAIL_LIKE = /[^\s@]+@[^\s@]+\.[^\s@]+/

/** What is wrong with a reason before it is sent, or nothing. */
export function reasonProblem(reason: string): 'empty' | 'email' | undefined {
  const r = reason.trim()
  if (!r) return 'empty'
  if (EMAIL_LIKE.test(r)) return 'email'
  return undefined
}

/** The capture an app user's form sends: their next `sessions`, open for `days`. */
export function captureBody(input: {
  hash: string
  sessions: number
  days: number
  reason: string
  /** Every family (the API's default) or all but replay; every family when absent. */
  detail?: CaptureDetail
  /** Email the person asking when it completes or expires; on when absent. */
  notify?: boolean
}): NewDeviceRequest {
  return {
    kind: 'capture',
    subject_kind: 'user',
    subject: input.hash,
    sessions: Math.min(
      CAPTURE_SESSIONS.max,
      Math.max(CAPTURE_SESSIONS.min, Math.round(input.sessions)),
    ),
    days: input.days,
    reason: input.reason.trim().slice(0, REASON_MAX),
    ...(input.detail === 'noReplay' ? { families: FAMILIES.filter((f) => f !== 'replay') } : {}),
    notify: input.notify !== false,
  }
}

/**
 * A capture request's state as the person page words it: pending until a device serves it,
 * capturing until it has released its sessions, then captured; or expired, replaced, canceled.
 */
export type CaptureStatus =
  | 'pending'
  | 'capturing'
  | 'captured'
  | 'expired'
  | 'replaced'
  | 'canceled'

export function captureStatus(r: DeviceRequest): CaptureStatus {
  const captured = r.answers?.captured ?? 0
  if (r.sessions !== null && captured >= r.sessions) return 'captured'
  if (r.state !== 'open') return r.state
  return (r.answers?.capturing ?? 0) > 0 ? 'capturing' : 'pending'
}

/** The person's open capture, which a new one replaces; the newest when there are several. */
export function openCapture(requests: readonly DeviceRequest[]): DeviceRequest | undefined {
  return requests
    .filter((r) => r.kind === 'capture' && r.state === 'open')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
}
